import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  parseReactivitiesToFloatArray,
  stripGaps,
  stripGapsReactivities,
} from './rfviewUtils';

const RFviewCanvas = forwardRef(function RFviewCanvas(
  { sequence, predictions, reference, reactivitiesStr, consensusStructure },
  ref
) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    switchTo(idx) {
      if (!containerRef.current) return;
      if (viewerRef.current._currentStructIdx === idx) return;
      const pills = containerRef.current.querySelectorAll('.rv-struct-pill');
      if (pills[idx]) pills[idx].click();
    },
  }), []);

  useEffect(() => {
    if (!containerRef.current || !sequence || predictions.length === 0) return;

    const rawReactivities = parseReactivitiesToFloatArray(reactivitiesStr);
    const hasReactivities = rawReactivities.length > 0;
    const cleanReactivities = hasReactivities
      ? stripGapsReactivities(sequence, rawReactivities)
      : null;

    const { sequence: cleanSequence } = stripGaps(sequence, predictions[0].structure);

    const structures = predictions.map((pred, i) => {
      const { structure } = stripGaps(sequence, pred.structure);
      const entry = { label: `#${i + 1}`, structure };
      if (cleanReactivities) {
        entry.values = cleanReactivities;
      }
      return entry;
    });

    if (consensusStructure) {
      const { structure } = stripGaps(sequence, consensusStructure);
      const entry = { label: 'Consensus', structure };
      if (cleanReactivities) {
        entry.values = cleanReactivities;
      }
      structures.push(entry);
    }

    if (reference) {
      const { structure } = stripGaps(sequence, reference);
      structures.push({ label: 'Reference', structure });
    }

    viewerRef.current = new window.RFviewJS(containerRef.current, {
      sequence: cleanSequence,
      structures,
      showColors: hasReactivities,
      buttons: {
        upload: false,
        rfam: false,
        manualInput: false,
        cleanOne: false,
        cleanAll: false,
        toolbarPos: false,
      },
    });

    return () => {
      viewerRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [sequence, predictions, reference, reactivitiesStr, consensusStructure]);

  return <div ref={containerRef} className="rfview-canvas" />;
});

export default RFviewCanvas;
